import { useIsomorphicLayoutEffect } from "@/hooks/Custom";
import {
  useAsyncBrands,
  useAsyncModelsByBrand,
  useAsyncYearsByModelBrand
} from "@/hooks/Vehicles/Enums";
import { Box, Flex, HStack, Stack, Text } from "@chakra-ui/layout";
import { Button, Skeleton, useBreakpointValue, useToast } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { useState } from "react";
import { Select2, Select2OptionProps } from "../Select2";
import Select from "react-select";
import { Select2Mobile } from "../Select2/indexmobile";

type SearchProps = {
  loading?: boolean;
  loadingAnim?: any;
};

export function VehicleSearchDesktop({ loading, loadingAnim }: SearchProps) {
  // ! Env Data
  const DefaultBrand = "VOLKSWAGEN";
  const DefaultModel = "POLO";
  const DefaultYear = "2023";

  // ! Initial States
  const [Brands, setBrands] = useState<Select2OptionProps[]>([]);
  const [SelectedBrand, setSelectedBrand] = useState<string>(DefaultBrand);
  const [Models, setModels] = useState<Select2OptionProps[]>([]);
  const [SelectedModel, setSelectedModel] = useState<string>(DefaultModel);
  const [Years, setYears] = useState<Select2OptionProps[]>([]);
  const [SelectedYear, setSelectedYear] = useState<string>(DefaultYear);

  // ! Controllers & Rullers
  const [ModelTrigger, setModelTrigger] = useState<number>(0);
  const [YearTrigger, setYearTrigger] = useState<number>(0);
  const Toast = useToast({
    position: "bottom",
    duration: 3000,
    isClosable: true
  });
  const Router = useRouter();

  // ! Methods
  async function requestBrands() {
    const [BData, BError] = await useAsyncBrands();
    if (BData && BError === null) {
      setBrands(
        BData?.map((brand: string) => ({
          label: brand,
          value: brand
        }))
      );
    }
  }
  async function requestModels(Brand: string) {
    const [MData, MError] = await useAsyncModelsByBrand(Brand);
    if (MData && MError === null) {
      setModels(
        MData?.map((model: string) => ({
          label: model,
          value: model
        }))
      );
    }
  }
  async function requestYears(Brand: string, Model: string) {
    const [YData, YError] = await useAsyncYearsByModelBrand(Brand, Model);
    if (YData && YError === null) {
      setYears(
        YData?.map((comp) => ({
          label: comp.year,
          value: comp.year
        }))
      );
    }
  }
  async function submitVehicleSearch(e = null) {
    Toast.closeAll();
    switch (true) {
      case !Boolean(SelectedBrand):
        Toast({
          title: "Selecione uma marca para realizar a pesquisa.",
          status: "info"
        });
        return false;
      case !Boolean(SelectedModel):
        Toast({
          title: "Selecione um modelo para realizar a pesquisa.",
          status: "info"
        });
        return false;
      default:
        loadingAnim(true);

        Router.push({
          pathname: "/pesquisa/veiculo",
          query: Boolean(e)
            ? {
                brand: `${SelectedBrand}`,
                model: `${SelectedModel}`,
                year: `${SelectedYear ?? e ?? ""}`
              }
            : {
                brand: `${SelectedBrand}`,
                model: `${SelectedModel}`
              }
        });
    }
  }

  // ! Effects
  useIsomorphicLayoutEffect(() => {
    requestBrands();
    requestModels(DefaultBrand);
    requestYears(DefaultBrand, DefaultModel);
  }, []);

  const isWideVersion = useBreakpointValue({
    base: false,
    xs: false,
    sm: false,
    md: true,
    lg: false,
    xl: true
  });

  const array = ["blue", "pink", "red"];
  return (
    <>
      <Flex w={"full"} flexDir={"column"} alignItems={"flex-start"}>
        {loading ? (
          <Flex w={"full"} h={"40px"} justifyContent={"space-between"}>
            <Skeleton w={"280px"} height={"40px"} borderRadius={"50px"} />
            <Skeleton w={"200px"} height={"40px"} borderRadius={"50px"} />
            <Skeleton w={"200px"} height={"40px"} borderRadius={"50px"} />
            <Skeleton w={"100px"} height={"40px"} borderRadius={"520px"} />
          </Flex>
        ) : (
          <>
            <Text
              textStyle="Regular"
              color="#58585A"
              ml={isWideVersion ? "0px" : "6.2rem"}
              mb={"8px"}
              mt="1rem"
            >
              Busque por veículo:
            </Text>
            <Flex w={"full"} h={"40px"} justifyContent={"space-between"}>
              <Flex w={"full"} justifyContent={"space-between"} mr={"10px"}>
                {/* BRAND SELECT */}
                <Select2
                  customW={"280px"}
                  OptionsData={Brands}
                  setSelected={setSelectedBrand}
                  Selected={SelectedBrand}
                  Skew={false}
                  HasImg={true}
                  Callback={(e: string) => {
                    setSelectedModel(null);
                    setSelectedYear(null);
                    requestModels(e);
                    setModelTrigger((prev) => prev + 1);
                  }}
                />
                {/* MODEL SELECT */}
                <Select2
                  customW={"200px"}
                  Skew={true}
                  OptionsData={Models}
                  setSelected={setSelectedModel}
                  Selected={SelectedModel}
                  Trigger={ModelTrigger}
                  Callback={(e: string) => {
                    setSelectedYear(null);
                    requestYears(SelectedBrand, e);
                    setYearTrigger((prev) => prev + 1);
                  }}
                />
                {/* YEAR SELECT */}
                <Select2
                  customW={"200px"}
                  Skew={false}
                  OptionsData={Years}
                  setSelected={setSelectedYear}
                  Selected={SelectedYear}
                  Trigger={YearTrigger}
                  HasNullOption={true}
                  Callback={(e: string) => {
                    setSelectedYear(e);
                  }}
                  IsLast={true}
                  Submiter={submitVehicleSearch}
                />
              </Flex>
              <Button
                variant="primary-button"
                bgColor={"ll-blue.main"}
                onClick={submitVehicleSearch}
                minW={"100px"}
                _hover={{ opacity: "0.5" }}
              >
                <Text textStyle="Regular" color="white">
                  Buscar
                </Text>
              </Button>
            </Flex>
          </>
        )}
      </Flex>
    </>
  );
}
export function VehicleSearchMobile({ loading, loadingAnim }: SearchProps) {
  // ! Env Data
  const DefaultBrand = "VOLKSWAGEN";
  const DefaultModel = "POLO";
  const DefaultYear = "2023";

  // ! Initial States
  const [Brands, setBrands] = useState<Select2OptionProps[]>([]);
  const [SelectedBrand, setSelectedBrand] = useState<string>(DefaultBrand);
  const [Models, setModels] = useState<Select2OptionProps[]>([]);
  const [SelectedModel, setSelectedModel] = useState<string>(DefaultModel);
  const [Years, setYears] = useState<Select2OptionProps[]>([]);
  const [SelectedYear, setSelectedYear] = useState<string>(DefaultYear);

  // ! Controllers & Rullers
  const [ModelTrigger, setModelTrigger] = useState<number>(0);
  const [YearTrigger, setYearTrigger] = useState<number>(0);
  const Toast = useToast({
    position: "bottom",
    duration: 3000,
    isClosable: true
  });
  const Router = useRouter();

  // ! Methods
  async function requestBrands() {
    const [BData, BError] = await useAsyncBrands();
    if (BData && BError === null) {
      setBrands(
        BData?.map((brand: string) => ({
          label: brand,
          value: brand
        }))
      );
    }
  }
  async function requestModels(Brand: string) {
    const [MData, MError] = await useAsyncModelsByBrand(Brand);
    if (MData && MError === null) {
      setModels(
        MData?.map((model: string) => ({
          label: model,
          value: model
        }))
      );
    }
  }
  async function requestYears(Brand: string, Model: string) {
    const [YData, YError] = await useAsyncYearsByModelBrand(Brand, Model);
    if (YData && YError === null) {
      setYears(
        YData?.map((comp) => ({
          label: comp.year,
          value: comp.year
        }))
      );
    }
  }
  async function submitVehicleSearch(e = null) {
    Toast.closeAll();
    switch (true) {
      case !Boolean(SelectedBrand):
        Toast({
          title: "Selecione uma marca para realizar a pesquisa.",
          status: "info"
        });
        return false;
      case !Boolean(SelectedModel):
        Toast({
          title: "Selecione um modelo para realizar a pesquisa.",
          status: "info"
        });
        return false;
      default:
        loadingAnim(true);
        Router.push({
          pathname: "/pesquisa/veiculo",
          query: Boolean(e)
            ? {
                brand: `${SelectedBrand}`,
                model: `${SelectedModel}`,
                year: `${SelectedYear ?? e ?? ""}`
              }
            : {
                brand: `${SelectedBrand}`,
                model: `${SelectedModel}`
              }
        });
    }
  }

  // ! Effects
  useIsomorphicLayoutEffect(() => {
    requestBrands();
    requestModels(DefaultBrand);
    requestYears(DefaultBrand, DefaultModel);
  }, []);

  return (
    <>
      <Stack alignItems={"center"} w={"full"}>
        <Flex justifyContent={"flex-start"} w={"full"}>
          <Text textStyle={"Regular"} color="#58585A">
            Busque pelo seu veículo:
          </Text>
        </Flex>

        {/* BRAND SELECT */}
        {loading ? (
          <Skeleton w="full" height={"40px"} borderRadius={"50px"} />
        ) : (
          <Select2Mobile
            customW="100%"
            OptionsData={Brands}
            setSelected={setSelectedBrand}
            Selected={SelectedBrand}
            Skew={false}
            HasImg={true}
            Callback={(e: string) => {
              setSelectedModel(null);
              setSelectedYear(null);
              requestModels(e);
              setModelTrigger((prev) => prev + 1);
            }}
          />
        )}

        <HStack w={"full"} mt={"10px"}>
          {loading ? (
            <>
              <Skeleton w="calc(100%/2)" height={"40px"} borderRadius={"50px"} />
              <Skeleton w="calc(100%/2)" height={"40px"} borderRadius={"50px"} />
            </>
          ) : (
            <>
              {/* MODEL SELECT */}
              <Select2Mobile
                customW={"calc(100%/2)"}
                Skew={true}
                OptionsData={Models}
                setSelected={setSelectedModel}
                Selected={SelectedModel}
                Trigger={ModelTrigger}
                Callback={(e: string) => {
                  setSelectedYear(null);
                  requestYears(SelectedBrand, e);
                  setYearTrigger((prev) => prev + 1);
                }}
              />
              {/* YEAR SELECT */}
              <Select2Mobile
                typeInput="number"
                customW={"calc(100%/2)"}
                Skew={false}
                OptionsData={Years}
                setSelected={setSelectedYear}
                Selected={SelectedYear}
                Trigger={YearTrigger}
                HasNullOption={true}
                Callback={(e: string) => {
                  setSelectedYear(e);
                }}
                IsLast={true}
                Submiter={submitVehicleSearch}
              />
            </>
          )}
        </HStack>
      </Stack>
      {loading ? (
        <Skeleton mt="1rem" w="full" height={"40px"} borderRadius={"50px"} />
      ) : (
        <Button
          variant={"primary-button"}
          bgColor={"ll-blue.main"}
          mt="1rem"
          w="full"
          aria-label={"Pesquisar"}
          onClick={submitVehicleSearch}
        >
          Buscar
        </Button>
      )}
    </>
  );
}
